import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BackupRecord } from '@panel/shared';
import { api } from '../lib/api.js';
import { bytes, fmtDate, relTime } from '../lib/format.js';
import { useStore } from '../lib/store.js';
import { IconBackup, IconChevronRight, IconDownload, IconSearch } from '../components/Icons.js';

const PAGE_SIZE = 25;

export default function Backups() {
  const servers = useStore((s) => s.servers);
  const allServers = useMemo(
    () => Array.from(servers.values()).sort((a, b) => a.Server_Name.localeCompare(b.Server_Name)),
    [servers],
  );

  const [serverFilter, setServerFilter] = useState<number | 'all'>('all');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{ backups: BackupRecord[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);

  // Reset to first page when the filter changes
  useEffect(() => { setPage(0); }, [serverFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      skip: String(page * PAGE_SIZE),
    });
    if (serverFilter !== 'all') params.set('Server_ID', String(serverFilter));
    api.get<{ backups: BackupRecord[]; total: number }>(`/api/backups?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [serverFilter, page]);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromIdx = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const toIdx = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Backups</h1>
        <p className="text-sm text-fg-muted mt-1 max-w-3xl">
          Global view of all backup attempts. Backups upload to Cloudflare R2;
          old ones auto-prune per the Hub's retention setting.
        </p>
      </header>

      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-fg-dim shrink-0"><IconSearch className="w-4 h-4" /></span>
          <select
            className="input !h-9 max-w-xs"
            value={serverFilter === 'all' ? 'all' : String(serverFilter)}
            onChange={(e) => setServerFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
            aria-label="Filter by server"
          >
            <option value="all">All servers</option>
            {allServers.map((s) => (
              <option key={s.Server_ID} value={s.Server_ID}>
                #{s.Server_ID} {s.Server_Name}
              </option>
            ))}
          </select>
        </label>
        <span className="text-xs text-fg-dim tabular-nums">
          {total === 0 ? 'No backups' : `${fromIdx}–${toIdx} of ${total}`}
        </span>
      </div>

      <div className="card overflow-hidden">
        {loading && !data ? (
          <div className="p-10 text-fg-muted text-center text-sm">Loading…</div>
        ) : !data || data.backups.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center gap-3">
            <div className="w-12 h-12 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
              <IconBackup className="w-5 h-5" />
            </div>
            <div className="text-sm font-medium text-fg">
              {serverFilter === 'all' ? 'No backups recorded yet' : 'No backups for this server'}
            </div>
            <p className="text-xs text-fg-muted max-w-xs">
              Backups appear automatically whenever a server runs one. You can also trigger one from a server's Actions panel.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th>Server</th>
                  <th className="hidden md:table-cell">Machine</th>
                  <th className="text-right">Size</th>
                  <th className="hidden lg:table-cell">Started</th>
                  <th className="hidden md:table-cell">Relative</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.backups.map((b) => (
                  <tr key={b.key}>
                    <td>
                      <span
                        className={`dot ${
                          b.status === 'complete' ? 'bg-status-online'
                            : b.status === 'failed' ? 'bg-status-error'
                            : 'bg-status-warn'
                        }`}
                        title={b.status}
                      />
                    </td>
                    <td>
                      <Link to={`/servers/${b.Server_ID}`} className="hover:text-accent transition-colors block truncate max-w-[260px]" title={b.Server_Name}>
                        <span className="font-mono text-fg-dim text-xs">#{b.Server_ID}</span>{' '}
                        <span className="text-fg">{b.Server_Name}</span>
                      </Link>
                    </td>
                    <td className="hidden md:table-cell text-fg-muted truncate max-w-[180px]">{b.Server_Machine}</td>
                    <td className="text-right text-fg-muted tabular-nums whitespace-nowrap">{bytes(b.size_bytes)}</td>
                    <td className="hidden lg:table-cell text-fg-muted text-xs whitespace-nowrap">{fmtDate(b.started_at)}</td>
                    <td className="hidden md:table-cell text-fg-dim text-xs whitespace-nowrap">{relTime(b.started_at)}</td>
                    <td className="text-right">
                      <button
                        onClick={async () => {
                          const { url } = await api.get<{ url: string }>(`/api/backups/${encodeURIComponent(b.key)}/download-url`);
                          window.open(url, '_blank');
                        }}
                        className="btn-quiet btn-sm"
                      >
                        <IconDownload className="w-3.5 h-3.5" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pageCount > 1 && (
          <div className="px-4 h-12 border-t border-bg-border flex items-center justify-between text-xs">
            <span className="text-fg-dim tabular-nums">Page {page + 1} of {pageCount}</span>
            <div className="flex items-center gap-1">
              <button
                className="btn-quiet btn-sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <IconChevronRight className="w-3.5 h-3.5 rotate-180" /> Previous
              </button>
              <button
                className="btn-quiet btn-sm"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1 || loading}
              >
                Next <IconChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
